<template>
  <section class="content-header">
    <div class="container-fluid">
      <div class="row mb-2">
        <div class="col-sm-6">
          <h1>Respaldos de Base de Datos</h1>
        </div>
        <div class="col-sm-6">
          <ol class="breadcrumb float-sm-right">
            <li class="breadcrumb-item"><router-link to="/">Home</router-link></li>
            <li class="breadcrumb-item active">Respaldos</li>
          </ol>
        </div>
      </div>
    </div>
  </section>

  <section class="content">
    <div class="container-fluid">
      <!-- Botones de Acción Global -->
      <div class="row mb-3">
        <div class="col-md-12 d-flex gap-2">
          <button 
            id="btn-generar-respaldo"
            class="btn btn-success" 
            @click="crearRespaldo" 
            :disabled="isLoading"
          >
            <i class="fas fa-plus-circle mr-1"></i> 
            {{ isLoading ? 'Procesando...' : 'Generar Nuevo Respaldo' }}
          </button>

          <button 
            id="btn-subir-respaldo" 
            class="btn btn-warning" 
            @click="triggerFileUpload" 
            :disabled="isLoading"
          >
            <i class="fas fa-upload mr-1"></i> Subir y Restaurar
          </button>
          
          <!-- Input oculto para subida de archivos -->
          <input 
            type="file" 
            ref="fileInput" 
            class="d-none" 
            accept=".sql,.zip,.gz" 
            @change="handleFileUpload"
          />
        </div>
      </div>

      <div class="card shadow-sm" id="card-listado-respaldos">
        <div class="card-header" >
          <h3 class="card-title">Listado de Respaldos Disponibles</h3>
          <div class="card-tools">
            <button type="button" class="btn btn-tool" @click="obtenerRespaldos">
              <i class="fas fa-sync-alt"></i>
            </button>
          </div>
        </div>
        
        <div class="card-body p-0">
          <table class="table table-hover">
            <thead>
              <tr>
                <th style="width: 10px"></th>
                <th>Nombre del Archivo</th>
                <th class="d-none d-md-table-cell">Tamaño</th>
                <th class="d-none d-md-table-cell">Fecha de Creación</th>
                <th class="text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="backups.length === 0">
                <td colspan="5" class="text-center text-muted py-4">
                  No hay respaldos disponibles.
                </td>
              </tr>
              <template v-for="(backup, index) in backups" :key="index">
                <tr>
                  <td>
                    <button class="btn btn-xs btn-light d-md-none" @click="backup.expanded = !backup.expanded">
                      <i class="fas" :class="backup.expanded ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                    </button>
                  </td>
                  <td class="text-truncate" style="max-width: 150px;">
                    <i class="fas fa-file-archive text-secondary mr-2"></i>
                    {{ backup.nombre }}
                  </td>
                  <td class="d-none d-md-table-cell">{{ backup.tamaño_kb }}</td>
                  <td class="d-none d-md-table-cell">{{ backup.fecha }}</td>
                  <td class="text-right">
                    <div class="btn-group">
                      <button 
                        class="btn btn-sm btn-info" 
                        @click="descargarRespaldo(backup.nombre)"
                        title="Descargar"
                      >
                        <i class="fas fa-download"></i>
                      </button>
                      <button 
                      
                        class="btn btn-sm btn-danger" 
                        @click="restaurarDesdeServidor(backup.nombre)"
                        title="Restaurar este punto"
                      >
                        <i class="fas fa-history"></i>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="backup.expanded" class="d-md-none bg-light">
                  <td colspan="5">
                    <div class="px-3 py-2">
                      <p class="mb-1"><strong>Tamaño:</strong> {{ backup.tamaño_kb }}</p>
                      <p class="mb-0"><strong>Fecha:</strong> {{ backup.fecha }}</p>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        
        <div v-if="isLoading" class="overlay dark">
          <i class="fas fa-2x fa-sync-alt fa-spin"></i>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance, onBeforeUnmount } from 'vue';
import api from '@/services/api';
import { useAuth } from "@/composables/useAuth";
import { tutorial } from "@/plugins/tutorial";
import { loading } from '@/plugins/loading';
import { alert } from '@/plugins/alert';

const { proxy } = getCurrentInstance();
const { logout } = useAuth();

const backups = ref([]);
const isLoading = ref(false);
const fileInput = ref(null);

// --- Cargar Respaldos ---
const obtenerRespaldos = async () => {
  isLoading.value = true;
  try {
    const { data } = await api.get('/respaldos');
    // Asumiendo que la API devuelve un array de objetos { name, size, date }
    backups.value = data; 
  } catch (error) {
    console.error(error);
    proxy.$toast.fire({ icon: 'error', title: 'Error al cargar respaldos' });
  } finally {
    isLoading.value = false;
  }
};

// --- Crear Nuevo Respaldo ---
const crearRespaldo = async () => {
  loading.show('Generando respaldo...');
  isLoading.value = true;
  try {
    await api.post('/respaldo');
    proxy.$toast.fire({ icon: 'success', title: 'Respaldo generado correctamente' });
    await obtenerRespaldos();
  } catch (error) {
    console.error(error);
    alert.show({ title: 'Error', text: 'No se pudo generar el respaldo', icon: 'error' });
  } finally {
    loading.hide();
    isLoading.value = false;
  }
};

// --- Descargar Respaldo ---
const descargarRespaldo = async (fileName) => {
  try {
    loading.show('Preparando descarga...');
    const response = await api.get(`/descargar-respaldo/${fileName}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/sql' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    proxy.$toast.fire({ icon: 'success', title: 'Descarga iniciada' });
  } catch (error) {
    alert.show({ title: 'Error', text: 'Error al descargar el archivo', icon: 'error' });
  } finally {
    loading.hide();
  }
};

// --- Restaurar desde Servidor ---
const restaurarDesdeServidor = (fileName) => {
  alert.show({
    title: '¿Estás seguro?',
    text: `Se restaurará la base de datos al punto: ${fileName}. Los datos actuales se perderán.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Sí, restaurar',
    cancelButtonText: 'Cancelar'
  }).then(async (result) => {
    if (result.isConfirmed) {
      loading.show('Restaurando sistema...');
      isLoading.value = true;
      try {
        await api.post('/restaurar-respaldo', { archivo: fileName });
        await alert.show({ title: '¡Restaurado!', text: 'El sistema ha sido restaurado con éxito. Por seguridad, debe iniciar sesión nuevamente.', icon: 'success' });
        
        // Desloguear y redirigir
        logout();
      } catch (error) {
        console.error(error);
        alert.show({ title: 'Error', text: 'No se pudo restaurar la base de datos.', icon: 'error' });
      } finally {
        loading.hide();
        isLoading.value = false;
      }
    }
  });
};

// --- Subir y Restaurar (Local) ---
const triggerFileUpload = () => {
  fileInput.value.click();
};

const handleFileUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Limpiar input para permitir subir el mismo archivo de nuevo si falla
  event.target.value = '';

  alert.show({
    title: 'Confirmar Restauración',
    text: `Vas a subir y restaurar el archivo "${file.name}". Esto sobrescribirá la base de datos actual.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    confirmButtonText: 'Sí, subir y restaurar'
  }).then(async (result) => {
    if (result.isConfirmed) {
      isLoading.value = true;
      const formData = new FormData();
      formData.append('archivo_sql', file);

      try {
        await api.post('/restaurar-respaldo-subido', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        await proxy.$alert({ title: '¡Éxito!', text: 'Base de datos restaurada. Por seguridad, debe iniciar sesión nuevamente.', icon: 'success' });
        
        // Desloguear y redirigir
        logout();
        
      } catch (error) {
        proxy.$alert({ title: 'Error', text: 'Falló la restauración del archivo subido.', icon: 'error' });
      } finally {
        isLoading.value = false;
      }
    }
  });
};

// --- Tutorial ---
const iniciarTutorial = () => {
  const steps = [
    {
      element: '#btn-generar-respaldo',
      title: 'Generar Respaldo',
      text: 'Crea una copia de seguridad actual de toda la base de datos.',
      position: 'bottom'
    },
    {
      element: '#btn-subir-respaldo',
      title: 'Subir y Restaurar',
      text: 'Permite cargar un archivo de respaldo desde tu computadora para restaurar el sistema.',
      position: 'bottom'
    },
    {
      element: '#card-listado-respaldos',
      title: 'Lista de Respaldos',
      text: 'En esta tabla se listan todos los archivos de respaldo disponibles en el servidor.',
      position: 'top'
    },
    {
      element: '.btn-group',
      title: 'Acciones de Archivo',
      text: 'Usa estos botones para descargar el archivo a tu equipo o restaurar la base de datos a ese punto específico.',
      position: 'left'
    }
  ];
  tutorial.start(steps);
};

onMounted(() => {
    obtenerRespaldos();
  tutorial.set(iniciarTutorial);
});

onBeforeUnmount(() => {
  tutorial.set(null);
});
</script>

<style scoped>
.gap-2 { gap: 0.5rem; }
</style>

<style>
/* Estilos globales para el tutorial si es necesario */
.hide-next-btn .introjs-nextbutton, 
.hide-next-btn .introjs-nextBtn {
  display: none !important;
}
</style>
